import React, {
  useEffect,
  useRef,
  useMemo,
  useContext,
  useState,
  useCallback,
} from 'react';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import SubFormTab from '../../SubFormTab/SubFormTab';
import RequestBadges from '../../../RequestBadges/RequestBadges';
import styles from '../FormSections.module.css';
import {
  useSubFormApproval,
  useCurrentStageStatus,
} from '../../../../../hooks';
import MultiFormContext, {
  useMultiForm,
} from '../../../../../contexts/MultiFormContext';
import $ from 'jquery';
import FormCrafterFormRenderer from '../../../FormCrafterFormRenderer/FormCrafterFormRenderer';
import '@iqvia-technology-solutions/formcrafter/dist/esm/styles/formcrafter.css';
import AddCommentSection from '../../../FormCommentSection/AddCommentSection';
import SubFormCommentsSection from '../../SubFormComments/SubFormCommentsSection';
import HcpLicensesList from '../../../../Guest/Requests/New_Requests/HcpLicensesList/HcpLicensesList';
import HcpLicensesListView from '../../../../Guest/Requests/New_Requests/HcpLicensesList/HcpLicensesListView';
import ViewOnlyFileDisplay from '../../../ViewOnlyFileDisplay';
import { convertToFormCrafterSchema } from '../../../../../utils/formCrafter/schemaUtils';
import { parseFormData } from '../../../../../utils/formCrafter/formDataUtils';
import { isFieldVisible } from '../../../../../utils/formCrafter/visibilityUtils';
import { isFileType } from '../../../../../utils/formCrafter/fieldTypeUtils';
import axiosInstance from '../../../../../axiosInstance';

/** Subform tab ids from API may be numbers; React state sometimes uses strings — match reliably. */
export function tabIdMatch(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function pickSubformValues(formDataBySubform, subformId) {
  if (subformId == null) return {};
  return (
    formDataBySubform[subformId] ?? formDataBySubform[String(subformId)] ?? {}
  );
}

function repeaterType(field) {
  const t = String(field?.type || '').toLowerCase();
  return t === 'repeater' || t === 'repeatable';
}

/**
 * Flatten schema fields into read-only rows; repeaters become one row per (row × child field).
 */
function appendFieldSummaryRows(field, scopeValues, rows, keyPrefix = '') {
  if (!field || field.type === 'divider' || field.type === 'hidden') return;
  if (field.type === 'heading' || field.type === 'paragraph') return;

  const fid = field.id || field.name;
  if (!fid) return;
  if (!isFieldVisible(field, scopeValues)) return;

  const pfx = keyPrefix ? `${keyPrefix}:` : '';

  if (field.type === 'tabs' && field.tabContent) {
    Object.entries(field.tabContent).forEach(([tabKey, tabFields]) => {
      if (!Array.isArray(tabFields)) return;
      tabFields.forEach((tf) => {
        appendFieldSummaryRows(
          tf,
          scopeValues,
          rows,
          pfx ? `${pfx}${tabKey}` : tabKey
        );
      });
    });
    return;
  }

  if (repeaterType(field)) {
    const raw =
      scopeValues[fid] ?? scopeValues[String(fid)] ?? scopeValues[field.name];
    const childFields = Array.isArray(field.fields) ? field.fields : [];
    const items = Array.isArray(raw) ? raw : [];

    if (items.length === 0) {
      rows.push({
        key: `${pfx}${fid}:empty`,
        label: 'Items',
        field,
        raw: '',
        emptyRepeater: true,
      });
      return;
    }

    items.forEach((rowObj, rowIdx) => {
      if (!rowObj || typeof rowObj !== 'object') return;
      const rowScope = { ...scopeValues, ...rowObj };
      childFields.forEach((child) => {
        if (!child || child.type === 'divider' || child.type === 'hidden')
          return;
        if (child.type === 'heading' || child.type === 'paragraph') return;

        if (repeaterType(child)) {
          appendFieldSummaryRows(
            child,
            rowScope,
            rows,
            `${pfx}${fid}[${rowIdx}]`
          );
          return;
        }

        const cid = child.id || child.name;
        if (!cid) return;
        if (!isFieldVisible(child, rowScope)) return;
        const childRaw =
          rowObj[cid] ?? rowObj[String(cid)] ?? rowObj[child.name];
        const childLabel = child.label || child.name || String(cid);
        rows.push({
          key: `${pfx}${fid}[${rowIdx}].${cid}`,
          label: `Item ${rowIdx + 1}: ${childLabel}`,
          field: child,
          raw: childRaw,
        });
      });
    });
    return;
  }

  const raw =
    scopeValues[fid] ?? scopeValues[String(fid)] ?? scopeValues[field.name];

  rows.push({
    key: `${pfx}${fid}`,
    label: field.label || field.name || String(fid),
    field,
    raw,
  });
}

function collectSummaryRows(schemaFields, values) {
  const rows = [];
  if (!Array.isArray(schemaFields)) {
    return rows;
  }
  for (const field of schemaFields) {
    appendFieldSummaryRows(field, values, rows, '');
  }
  return rows;
}

function isFileLikeObject(obj) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    return false;
  }
  if (obj instanceof File) {
    return true;
  }
  const name =
    obj.original_name ||
    obj.originalName ||
    obj.filename ||
    obj.file_name ||
    obj.fileName ||
    (typeof obj.name === 'string' && obj.name.trim() !== '' ? obj.name : null);
  const id = obj.id ?? obj.file_id ?? obj.fileId;
  if (
    name &&
    (id != null ||
      obj.mime_type != null ||
      obj.mimeType != null ||
      obj.file_size != null ||
      obj.fileSize != null)
  ) {
    return true;
  }
  if (
    name &&
    (typeof obj.url === 'string' || typeof obj.file_path === 'string')
  ) {
    return true;
  }
  if (id != null && (name || obj.mime_type != null)) {
    return true;
  }
  return false;
}

/** One attachment row (repeater item_field slot or top-level file value). */
function formatSingleFileLike(obj) {
  if (obj == null) {
    return '';
  }
  if (typeof obj !== 'object') {
    return String(obj);
  }
  if (Array.isArray(obj.item_field) && obj.item_field.length > 0) {
    const nested = obj.item_field
      .map((slot) => formatSingleFileLike(slot))
      .filter(Boolean)
      .join(', ');
    if (nested) {
      return nested;
    }
  }
  const label =
    obj.original_name ||
    obj.originalName ||
    obj.filename ||
    obj.file_name ||
    obj.fileName ||
    (typeof obj.name === 'string' ? obj.name : '') ||
    (obj.id != null ? `File #${obj.id}` : '');
  return String(label || '').trim();
}

/** Shapes for {@link ViewOnlyFileDisplay} (same contract as License Information). */
function toViewOnlyFileDescriptors(raw, field) {
  const treatAsFile =
    isFileType(field?.type) ||
    (Array.isArray(raw) && raw.some((x) => isFileLikeObject(x))) ||
    (raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      isFileLikeObject(raw));

  if (!treatAsFile) {
    return [];
  }

  const items = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const out = [];
  for (const item of items) {
    if (item == null) continue;
    if (typeof item === 'string') {
      continue;
    }
    if (typeof item === 'object' && isFileLikeObject(item)) {
      const id = item.id ?? item.file_id ?? item.fileId;
      if (id == null) continue;
      const name =
        item.original_name ||
        item.originalName ||
        item.filename ||
        item.file_name ||
        item.fileName ||
        (typeof item.name === 'string' ? item.name : null) ||
        `File ${id}`;
      out.push({
        id,
        file_id: id,
        name,
        file_name: name,
        file_size: item.file_size ?? item.fileSize,
        mime_type: item.mime_type ?? item.mimeType,
      });
    }
  }
  return out;
}

function formatReadOnlyPriceLike(raw, field, scopeValues) {
  const codeFromRow =
    raw && typeof raw === 'object'
      ? raw.currency || raw.currencyCode || raw.code || ''
      : '';
  const amountFromRow =
    raw && typeof raw === 'object' ? (raw.amount ?? raw.value ?? '') : '';
  const fid = field?.id || field?.name;
  const currencyKey = fid ? `${fid}_currency` : null;
  const codeFromFlat =
    currencyKey && scopeValues && typeof scopeValues === 'object'
      ? (scopeValues[currencyKey] ?? scopeValues[String(currencyKey)])
      : '';
  const currency = (codeFromRow || codeFromFlat || '').toString().trim();
  const amount =
    amountFromRow !== '' && amountFromRow != null
      ? amountFromRow
      : typeof raw === 'string' || typeof raw === 'number'
        ? raw
        : '';
  if (
    amount === '' ||
    amount === null ||
    amount === undefined ||
    String(amount).trim() === ''
  ) {
    return currency || '-';
  }
  const n =
    typeof amount === 'number'
      ? amount
      : Number(String(amount).replace(/,/g, ''));
  if (!Number.isNaN(n)) {
    const dec =
      field?.decimalPlaces !== undefined ? Number(field.decimalPlaces) : 2;
    const places = Number.isFinite(dec) ? dec : 2;
    const formatted = n.toFixed(places);
    return currency ? `${currency} ${formatted}`.trim() : formatted;
  }
  return currency ? `${currency} ${amount}`.trim() : String(amount);
}

function formatReadOnlyFieldValue(raw, field, scopeValues) {
  if (raw === undefined || raw === null || raw === '') {
    return '-';
  }

  const ft = String(field?.type || '').toLowerCase();
  if (ft === 'price') {
    return formatReadOnlyPriceLike(raw, field, scopeValues);
  }

  const treatAsFile = isFileType(field?.type);

  if (Array.isArray(raw)) {
    if (raw.length === 0) {
      return '-';
    }

    const allPrimitive = raw.every(
      (x) => x == null || ['string', 'number', 'boolean'].includes(typeof x)
    );
    if (allPrimitive) {
      const joined = raw
        .map((x) => (x == null ? '' : String(x)))
        .filter((s) => s !== '')
        .join(', ');
      return joined || '-';
    }

    const fileParts = raw
      .map((x) => {
        if (x == null) {
          return null;
        }
        if (typeof x !== 'object') {
          return String(x);
        }
        if (Object.keys(x).length === 0) {
          return null;
        }
        if (treatAsFile || isFileLikeObject(x)) {
          const s = formatSingleFileLike(x);
          return s || null;
        }
        return null;
      })
      .filter(Boolean);

    if (fileParts.length > 0) {
      return fileParts.join(', ');
    }

    const first = raw[0];
    if (typeof first === 'object' && first !== null) {
      const looksLikeFile =
        first.original_name != null ||
        first.originalName != null ||
        first.filename != null ||
        (first.name != null && (first.id != null || first.file_id != null));
      if (looksLikeFile) {
        const parts = raw.map((x) => formatSingleFileLike(x)).filter(Boolean);
        return parts.length ? parts.join(', ') : '-';
      }
    }

    try {
      const s = JSON.stringify(raw);
      return s.length > 200 ? `${s.slice(0, 200)}…` : s;
    } catch {
      return '-';
    }
  }

  if (typeof raw === 'object') {
    if (treatAsFile || isFileLikeObject(raw)) {
      const s = formatSingleFileLike(raw);
      return s || '-';
    }
    try {
      const s = JSON.stringify(raw);
      return s.length > 200 ? `${s.slice(0, 200)}…` : s;
    } catch {
      return String(raw);
    }
  }
  const t = String(field?.type || '').toLowerCase();
  if (t === 'date' || t === 'datetime-local' || t === 'datetime') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime())
      ? String(raw)
      : d.toLocaleDateString('en-GB');
  }
  return String(raw);
}

/** Read-only label / value grid (aligned with License Information) for FormCrafter subforms. */
const SubformReadOnlySummary = ({
  activeForm,
  filledForm,
  persona = 'enduser',
}) => {
  const schema = useMemo(
    () => convertToFormCrafterSchema(activeForm?.json_object, activeForm?.id),
    [activeForm?.json_object, activeForm?.id]
  );
  const formDataBySubform = useMemo(
    () => parseFormData(filledForm, filledForm?.form_id ?? null),
    [filledForm]
  );
  const values = useMemo(
    () => pickSubformValues(formDataBySubform, activeForm?.id),
    [formDataBySubform, activeForm?.id]
  );
  const rows = useMemo(
    () => collectSummaryRows(schema?.fields || [], values),
    [schema?.fields, values]
  );

  if (!rows.length) {
    return (
      <Typography style={{ padding: '0.5rem 0' }} color="textSecondary">
        No responses saved for this section.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {rows.map(({ key, label, raw, field, emptyRepeater }) => {
        const fileDescriptors = emptyRepeater
          ? []
          : toViewOnlyFileDescriptors(raw, field);
        return (
          <Grid item xs={12} md={6} key={key}>
            <Typography variant="caption" color="textSecondary">
              {label}
            </Typography>
            {emptyRepeater ? (
              <Typography variant="body1">-</Typography>
            ) : fileDescriptors.length > 0 ? (
              <ViewOnlyFileDisplay
                files={fileDescriptors}
                label=""
                helperText="Download attachment"
                persona={persona === 'admin' ? 'admin' : 'enduser'}
              />
            ) : (
              <Typography variant="body1">
                {formatReadOnlyFieldValue(raw, field, values)}
              </Typography>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

/** Map MultiForm HCP state to the shape HcpLicensesListView expects when API requestDetails lags. */
const hcpStateToHcpViewData = (s) => {
  if (!s) {
    return null;
  }
  const hasId = s.id != null;
  const hasText =
    (s.hcp_name && String(s.hcp_name).trim()) ||
    (s.license_number && String(s.license_number).trim());
  if (!hasId && !hasText) {
    return null;
  }
  const f = s.license_image_file;
  const firstFile = Array.isArray(f) && f[0] ? f[0] : null;
  return {
    id: s.id,
    hcp_name: s.hcp_name,
    hcp_emirates_id: s.hcp_emirates_id,
    hcp_email: s.hcp_email,
    hcp_phone: s.hcp_phone,
    license_number: s.license_number,
    license_issue_date: s.license_issue_date,
    license_end_date: s.license_end_date,
    total_cme_hours: s.total_cme_hours,
    licenseImage:
      firstFile && (firstFile.id || firstFile.original_name)
        ? {
            id: firstFile.id,
            original_name: firstFile.original_name || firstFile.name,
            file_size: firstFile.file_size,
            mime_type: firstFile.mime_type,
          }
        : null,
    additionalAttachmentFiles: Array.isArray(s.additional_attachments)
      ? s.additional_attachments.map((a) => ({
          id: a.id,
          original_name: a.original_name || a.name,
          file_size: a.file_size,
          mime_type: a.mime_type,
        }))
      : [],
  };
};

// Not wrapped in React.memo: must re-render when MultiFormContext updates (HCP + sendback details).
const CustomFormRenderer = ({
  activeFormId,
  editable,
  isSendbackMode,
  requestDetails,
  persona,
  roleName,
  requestProgress,
  requestId,
  customFormApprovalProps,
  HcpLicensesList,
  HcpLicensesListView,
}) => {
  const multiFormCtx = useContext(MultiFormContext);
  const hcpViewData = useMemo(() => {
    if (requestDetails?.hcpLicensesList) {
      return requestDetails.hcpLicensesList;
    }
    return hcpStateToHcpViewData(multiFormCtx?.hcpLicensesList);
  }, [requestDetails?.hcpLicensesList, multiFormCtx?.hcpLicensesList]);

  if (activeFormId === 'custom_hcp_licenses_list') {
    if (editable) {
      return <HcpLicensesList isSendbackMode={isSendbackMode} />;
    } else {
      return (
        <HcpLicensesListView
          hcpLicensesListData={hcpViewData}
          persona={persona}
          isSendbackMode={isSendbackMode}
          role={roleName}
          requestProgressStatus={requestProgress?.status || ''}
          requestProgress={requestProgress}
          requestId={requestId}
          {...customFormApprovalProps}
        />
      );
    }
  }

  return null;
};

CustomFormRenderer.displayName = 'CustomFormRenderer';

/** Single-subform panel: FormCrafter renderer + comments (declarative; avoid nested createRoot on React-owned nodes). */
const FormCrafterSectionPanel = ({
  activeForm,
  filledForm,
  disabled,
  handleAddComment,
  requestProgressId,
  persona,
  requestId,
  enableAddComment,
  currentStageStatus,
  rendererRef,
  onUnmountPersist,
  hideFormRenderer = false,
}) => {
  let enableAddCommentEff = enableAddComment;
  if (currentStageStatus?.status === 'approved') {
    enableAddCommentEff = false;
  }
  const effFilled = filledForm || { value: '{}' };

  /** Eager upload so JSON-serialized `formSections` never contains raw `File` (which stringify as `{}`). */
  const handleStagedFileUpload = useCallback(async (file, options) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axiosInstance.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        onUploadProgress: (event) => {
          const cb = options?.onProgress;
          if (cb && event.total) {
            cb(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      if (!response.data?.success) {
        return null;
      }
      const serverFile = response.data.data;
      return {
        id: serverFile.id,
        stagedId: serverFile.id,
        name: serverFile.original_name || serverFile.name || file.name,
        type: serverFile.mime_type || file.type,
        size: Number(serverFile.file_size || serverFile.size || file.size || 0),
        isStagedFile: true,
      };
    } catch (e) {
      console.error('[FormCrafterSectionPanel] file upload failed:', e);
      return null;
    }
  }, []);

  const handleStagedFileDelete = useCallback(async () => {
    // Server cleanup can be added when a delete endpoint is wired; local list already updates.
  }, []);

  const eagerUploadEnabled = !disabled;

  return (
    <>
      {!hideFormRenderer ? (
        <FormCrafterFormRenderer
          ref={rendererRef}
          formId={filledForm?.form_id ?? null}
          subforms={[activeForm]}
          filledForm={effFilled}
          disabled={disabled}
          showSubmitButton={false}
          validateOnBlur={!disabled}
          onUnmountPersist={onUnmountPersist}
          eagerUploadEnabled={eagerUploadEnabled}
          onStagedFileUpload={handleStagedFileUpload}
          onStagedFileDelete={handleStagedFileDelete}
        />
      ) : null}
      <div
        className="form-comment-section"
        style={{
          marginTop: '35px',
          isolation: 'isolate',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {enableAddCommentEff ? (
          <AddCommentSection
            formData={activeForm}
            handleAddComment={handleAddComment}
            requestProgressId={requestProgressId}
            persona={persona}
            requestId={requestId}
          />
        ) : (
          requestId &&
          requestProgressId && (
            <SubFormCommentsSection
              persona={persona}
              requestId={requestId}
              subFormId={activeForm.id}
              requestProgressId={requestProgressId}
            />
          )
        )}
      </div>
    </>
  );
};

// New component that renders only the active form
export const FormContent = ({
  forms,
  activeTab,
  filledForm,
  handleAddComment,
  requestProgressId,
  persona,
  requestId,
  enableAddComment = true,
  children,
  requestProgress,
  disabled = true,
  errors = null,
}) => {
  const { data: currentStageStatus } = useCurrentStageStatus(
    requestId,
    persona
  );

  const activeForm = forms.find((form) => tabIdMatch(form.id, activeTab));

  const filledForRenderer = filledForm ?? { value: '{}' };

  return (
    <>
      <div className="vertical-tab-panel">
        <div className="form-section-container">
          {activeForm ? (
            <>
              {disabled ? (
                <Box sx={{ marginBottom: 3 }}>
                  <SubformReadOnlySummary
                    activeForm={activeForm}
                    filledForm={filledForRenderer}
                    persona={persona}
                  />
                </Box>
              ) : null}
              <FormCrafterSectionPanel
                activeForm={activeForm}
                filledForm={filledForRenderer}
                disabled={disabled}
                handleAddComment={handleAddComment}
                requestProgressId={requestProgressId}
                persona={persona}
                requestId={requestId}
                enableAddComment={enableAddComment}
                currentStageStatus={currentStageStatus}
                rendererRef={null}
                hideFormRenderer={disabled}
              />
            </>
          ) : null}
        </div>
      </div>
      {children}
    </>
  );
};

/**
 * Keeps in-progress FormCrafter data when the active vertical tab switches between
 * workflow subforms and custom tabs (e.g. License Information). Previously the cache
 * lived inside EditableFormContent, which unmounts entirely on custom tabs, so edits were lost.
 */
function useSubformFilledFormCache({
  filledForm,
  activeTab,
  editable,
  onFormDataChange,
}) {
  const formDataCacheRef = useRef({});
  const formRendererRef = useRef(null);
  const [cacheBump, setCacheBump] = useState(0);
  const prevFilledFormIdRef = useRef(filledForm?.id);

  const onFormDataChangeRef = useRef(onFormDataChange);
  onFormDataChangeRef.current = onFormDataChange;
  const filledFormRef = useRef(filledForm);
  filledFormRef.current = filledForm;

  useEffect(() => {
    if (prevFilledFormIdRef.current !== filledForm?.id) {
      prevFilledFormIdRef.current = filledForm?.id;
      formDataCacheRef.current = {};
      setCacheBump((b) => b + 1);
    }
  }, [filledForm?.id]);

  const filledValueStr =
    filledForm && typeof filledForm.value === 'string'
      ? filledForm.value
      : (filledForm?.value ?? '');

  const mergedFilledForm = useMemo(() => {
    void filledForm?.id;
    void filledValueStr;
    const latest = filledFormRef.current;
    if (!editable) {
      return latest;
    }
    void cacheBump;
    const baseFilled = latest || { value: '{}' };
    try {
      const cached = formDataCacheRef.current;
      const cacheKeys = Object.keys(cached || {});
      if (cacheKeys.length === 0) {
        return baseFilled;
      }
      const originalData = baseFilled?.value
        ? JSON.parse(baseFilled.value)
        : {};
      const mergedData = { ...originalData };
      for (const tabKey of cacheKeys) {
        const slice = cached[tabKey];
        if (
          slice &&
          typeof slice === 'object' &&
          Object.keys(slice).length > 0
        ) {
          mergedData[tabKey] = {
            ...(mergedData[tabKey] || {}),
            ...slice,
          };
        }
      }
      return { ...baseFilled, value: JSON.stringify(mergedData) };
    } catch (error) {
      console.error(
        'Error merging cached subform data into filledForm:',
        error
      );
      return baseFilled;
    }
  }, [filledForm?.id, filledValueStr, editable, cacheBump]);

  const handleUnmountPersist = useCallback((collected) => {
    if (!collected || typeof collected !== 'object') return;
    let changed = false;
    for (const [rawKey, slice] of Object.entries(collected)) {
      const prevKey = String(rawKey);
      if (slice && typeof slice === 'object' && Object.keys(slice).length > 0) {
        const nextStr = JSON.stringify(slice);
        const prevSlice = formDataCacheRef.current[prevKey];
        const prevStr = prevSlice ? JSON.stringify(prevSlice) : null;
        if (prevStr !== nextStr) {
          formDataCacheRef.current[prevKey] = slice;
          changed = true;
        }
      }
    }
    if (changed) setCacheBump((b) => b + 1);
  }, []);

  useEffect(() => {
    if (!editable) return;
    const register = onFormDataChangeRef.current;
    if (typeof register !== 'function') return;
    const getter = () => {
      if (formRendererRef.current?.getCollectedData) {
        try {
          const collected = formRendererRef.current.getCollectedData();
          if (collected && typeof collected === 'object') {
            for (const [subId, slice] of Object.entries(collected)) {
              if (
                slice &&
                typeof slice === 'object' &&
                Object.keys(slice).length > 0
              ) {
                formDataCacheRef.current[String(subId)] = slice;
              }
            }
          }
        } catch (error) {
          console.error('Error saving current form data:', error);
        }
      }
      return formDataCacheRef.current;
    };
    register(getter);
  }, [editable, activeTab]);

  return {
    mergedFilledForm,
    formRendererRef,
    handleUnmountPersist,
    subformRenderKey: cacheBump,
  };
}

// Base layout component with support for custom content rendering
export const FormSectionsLayout = ({
  forms,
  activeTab,
  onTabChange,
  isLoading: externalLoadingState,
  prevComments,
  requestDetails,
  requestProgressId,
  persona,
  requestProgress,
  children,
  customContent = false,
  headerTitle = 'Main Menu',
  showRequestBadges = true,
  filledForm,
  handleAddComment,
  requestId,
  enableAddComment = true,
  subformActionButtons = null,
  formActionButtons = null,
  formsWithApprovalStatus = null,
  editable = false,
  onFormDataChange = null,
  isCheckingApprovals = false,
  skipApprovalFetch = false, // Flag to skip internal approval fetching
  errors = null,
  hasHcpLicensesList = false,
  isSendbackMode = false,
  roleName = '', // Add roleName for passing to custom form views
  customFormApprovalProps = null, // Approval props for custom forms
  /** When set, forms with `isHcpProfileMenu` render this in the right panel (customer request flow). */
  renderHcpProfileMenuContent = null,
  /** Admin: comments + chrome below embedded profile (read-only / same as License Information pattern). */
  hcpProfileReviewerFooter = null,
}) => {
  // Only use the hook if not skipping and no approval status provided
  const shouldFetchInternally = !skipApprovalFetch && !formsWithApprovalStatus;

  const {
    subForms: subFormsWithApproval,
    isCheckingApprovals: internalCheckingApprovals,
  } = useSubFormApproval({
    enduserFilledFormId: shouldFetchInternally ? filledForm?.id : null,
    subForms: shouldFetchInternally ? forms : [],
    persona: shouldFetchInternally ? persona : null,
  });

  // Use provided forms with approval status, or internally fetched ones
  const formsToRender =
    formsWithApprovalStatus ||
    (shouldFetchInternally ? subFormsWithApproval : forms);

  // When parent supplies per-tab approval/comment flags, do not block icons behind
  // subform approval loading (profile + license tabs manage their own state).
  const isLoading =
    externalLoadingState !== undefined
      ? externalLoadingState
      : formsWithApprovalStatus
        ? false
        : skipApprovalFetch
          ? isCheckingApprovals
          : internalCheckingApprovals;

  const {
    mergedFilledForm,
    formRendererRef,
    handleUnmountPersist,
    subformRenderKey,
  } = useSubformFilledFormCache({
    filledForm,
    activeTab,
    editable,
    onFormDataChange,
  });

  // check if active tab is a custom form
  const activeForm = formsToRender.find((form) =>
    tabIdMatch(form.id, activeTab)
  );
  const isCustomForm = activeForm?.isCustom === true;
  const isProfileAppendTab =
    activeForm?.isHcpProfileMenu === true &&
    typeof renderHcpProfileMenuContent === 'function';

  /** Last non-custom vertical tab — keeps FormCrafter mounted (hidden) on License Information (monolith MultiFormView pattern). */
  const lastWorkflowSubformTabRef = useRef(null);
  const firstWorkflowSubformId = useMemo(
    () =>
      formsToRender.find((f) => !f?.isCustom && !f?.isHcpProfileMenu)?.id ??
      null,
    [formsToRender]
  );

  useEffect(() => {
    const match = formsToRender.find((form) => tabIdMatch(form.id, activeTab));
    if (match && !match.isCustom && !match.isHcpProfileMenu) {
      lastWorkflowSubformTabRef.current = activeTab;
    }
  }, [activeTab, formsToRender]);

  const editableWorkflowTab =
    editable && (isCustomForm || isProfileAppendTab)
      ? (lastWorkflowSubformTabRef.current ?? firstWorkflowSubformId)
      : activeTab;

  if (!formsToRender || formsToRender.length === 0) {
    return (
      <div className={styles.verticalTabsContainer}>
        <Typography style={{ padding: '1rem' }}>
          There are no subforms to display.
        </Typography>
      </div>
    );
  }
  return (
    <>
      <div className={styles.verticalTabsContainer}>
        <div className={styles.verticalTabItemsContainer}>
          <Typography className={styles.verticalTabHeader}>
            {headerTitle}
          </Typography>
          {formsToRender.map((form) => (
            <SubFormTab
              key={form.id}
              form={form}
              isActive={tabIdMatch(activeTab, form.id)}
              onClick={onTabChange}
              isLoading={isLoading}
              prevComments={prevComments}
              errors={errors}
            />
          ))}
        </div>

        <div className={styles.formSectionStyleContainer}>
          {showRequestBadges && (
            <RequestBadges
              referenceNumber={requestDetails?.reference_number}
              requestProgressId={requestProgressId}
              persona={persona}
              requestId={requestId}
            />
          )}

          {/* hide title header for custom forms as they have their own internal headers */}
          {!isCustomForm && (
            <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: '#333',
                paddingBottom: 1,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              {
                formsToRender?.find((form) => tabIdMatch(form.id, activeTab))
                  ?.name
              }
            </Typography>
          )}

          {customContent ? (
            children
          ) : editable ? (
            <>
              {isCustomForm && (
                <React.Suspense fallback={<div>Loading...</div>}>
                  <CustomFormRenderer
                    activeFormId={activeForm.id}
                    editable={editable}
                    isSendbackMode={isSendbackMode}
                    requestDetails={requestDetails}
                    persona={persona}
                    roleName={roleName}
                    requestProgress={requestProgress}
                    requestId={requestId}
                    customFormApprovalProps={customFormApprovalProps}
                    HcpLicensesList={HcpLicensesList}
                    HcpLicensesListView={HcpLicensesListView}
                  />
                </React.Suspense>
              )}
              {isProfileAppendTab && renderHcpProfileMenuContent(activeTab)}
              {isProfileAppendTab && hcpProfileReviewerFooter}
              <div
                style={
                  isCustomForm || isProfileAppendTab
                    ? { display: 'none' }
                    : undefined
                }
                aria-hidden={
                  isCustomForm || isProfileAppendTab ? true : undefined
                }
              >
                <EditableFormContent
                  forms={formsToRender}
                  activeTab={editableWorkflowTab}
                  filledForm={mergedFilledForm}
                  formRendererRef={formRendererRef}
                  onUnmountPersist={handleUnmountPersist}
                  subformRenderKey={subformRenderKey}
                  handleAddComment={handleAddComment}
                  requestProgressId={requestProgressId}
                  persona={persona}
                  requestId={requestId}
                  enableAddComment={enableAddComment}
                  children={children}
                  requestProgress={requestProgress}
                  disabled={false}
                  errors={errors}
                >
                  {!isProfileAppendTab ? subformActionButtons : null}
                </EditableFormContent>
              </div>
              {isProfileAppendTab ? subformActionButtons : null}
            </>
          ) : isCustomForm ? (
            <React.Suspense fallback={<div>Loading...</div>}>
              <CustomFormRenderer
                activeFormId={activeForm.id}
                editable={editable}
                isSendbackMode={isSendbackMode}
                requestDetails={requestDetails}
                persona={persona}
                roleName={roleName}
                requestProgress={requestProgress}
                requestId={requestId}
                customFormApprovalProps={customFormApprovalProps}
                HcpLicensesList={HcpLicensesList}
                HcpLicensesListView={HcpLicensesListView}
              />
            </React.Suspense>
          ) : isProfileAppendTab ? (
            <>
              {renderHcpProfileMenuContent(activeTab)}
              {hcpProfileReviewerFooter}
              {subformActionButtons}
            </>
          ) : (
            <>
              <FormContent
                forms={formsToRender}
                activeTab={activeTab}
                filledForm={filledForm}
                handleAddComment={handleAddComment}
                requestProgressId={requestProgressId}
                persona={persona}
                requestId={requestId}
                enableAddComment={enableAddComment}
                children={children}
                requestProgress={requestProgress}
                errors={errors}
              >
                {subformActionButtons}
              </FormContent>
            </>
          )}
        </div>
      </div>

      {formActionButtons && (
        <div className={styles.formLevelActionsContainer}>
          {formActionButtons}
        </div>
      )}
    </>
  );
};

// Export EditableFormContent at the end of the file

// Helper to extract form values from a rendered form
const extractFormValues = (container, subformId) => {
  if (!container || !document.contains(container)) {
    console.warn('Container not found or not in document');
    return {};
  }

  const formValues = {};

  try {
    const inputs = $(container).find(':input');

    inputs.each(function () {
      try {
        // Check if element still exists in DOM
        if (!this || !document.contains(this)) return;

        const inputType = $(this).attr('type');
        const name = $(this).attr('name');
        if (!name) return;

        switch (inputType) {
          case 'checkbox':
            if (!formValues[name]) formValues[name] = [];
            if ($(this).is(':checked')) formValues[name].push($(this).val());
            break;
          case 'radio':
            if ($(this).is(':checked')) formValues[name] = $(this).val();
            break;
          case 'file':
            if (this.files) formValues[name] = this.files;
            break;
          default:
            const value = $(this).val();
            if (value !== undefined) formValues[name] = value;
        }
      } catch (err) {
        console.error('Error processing input:', err);
      }
    });

    // Handle select elements
    $(container)
      .find('select')
      .each(function () {
        try {
          if (!this || !document.contains(this)) return;

          const name = $(this).attr('name');
          if (!name) return;

          $(this).attr('multiple');
          const value = $(this).val();
          if (value !== undefined && value !== null) {
            formValues[name] = value;
          }
        } catch (err) {
          console.error('Error processing select:', err);
        }
      });
  } catch (error) {
    console.error('Error extracting form values:', error);
  }

  return formValues;
};

const EditableFormContentBase = ({
  forms,
  activeTab,
  filledForm,
  formRendererRef,
  onUnmountPersist,
  subformRenderKey = 0,
  handleAddComment,
  requestProgressId,
  persona,
  requestId,
  enableAddComment = true,
  children,
  requestProgress,
  disabled = false,
  errors = null,
}) => {
  const { data: currentStageStatus } = useCurrentStageStatus(
    requestId,
    persona
  );

  const activeForm = forms.find((form) => tabIdMatch(form.id, activeTab));

  return (
    <>
      <div className="vertical-tab-panel">
        <div className="form-section-container">
          {activeForm ? (
            <FormCrafterSectionPanel
              key={`${String(activeTab)}-${subformRenderKey}`}
              activeForm={activeForm}
              filledForm={filledForm}
              disabled={disabled}
              handleAddComment={handleAddComment}
              requestProgressId={requestProgressId}
              persona={persona}
              requestId={requestId}
              enableAddComment={enableAddComment}
              currentStageStatus={currentStageStatus}
              rendererRef={formRendererRef}
              onUnmountPersist={onUnmountPersist}
            />
          ) : null}
        </div>
      </div>
      {children}
    </>
  );
};

export const EditableFormContent = EditableFormContentBase;

// Generic utility functions for real-time validation (DRY principle)

// Generic function to display field errors for any form type
export const displayGenericFieldErrors = (container, formType, fieldErrors) => {
  if (!container || !fieldErrors) return;

  Object.keys(fieldErrors).forEach((fieldName) => {
    try {
      const fieldError = fieldErrors[fieldName];
      if (!fieldError || !fieldError.error) return;

      // Find input by name, id, or data attribute
      let input = $(container).find(`[name="${fieldName}"]`).first();
      if (input.length === 0) {
        input = $(container).find(`#${fieldName}`).first();
      }
      if (input.length === 0) {
        input = $(container).find(`[data-field="${fieldName}"]`).first();
      }

      if (input.length > 0) {
        // Remove any existing error message for this field
        input
          .closest(
            '.form-group, .form-field, .field-wrapper, .MuiFormControl-root'
          )
          .find('.field-error-message')
          .remove();

        // Create error message element with escaped content
        const errorText = String(fieldError.error).replace(
          /[<>&"']/g,
          (match) => {
            const entities = {
              '<': '&lt;',
              '>': '&gt;',
              '&': '&amp;',
              '"': '&quot;',
              "'": '&#x27;',
            };
            return entities[match];
          }
        );

        const errorElement = $(`
          <div class="field-error-message" data-field="${fieldName}" style="
            color: #d32f2f;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 4px;
            line-height: 1.4;
          ">
            <span style="color: #d32f2f; flex-shrink: 0;">⚠</span>
            <span>${errorText}</span>
          </div>
        `);

        // Insert error message - try different positioning strategies
        const formGroup = input.closest(
          '.form-group, .form-field, .field-wrapper, .MuiFormControl-root'
        );
        if (formGroup.length > 0) {
          formGroup.append(errorElement);
        } else {
          const parentDiv =
            input.parent('div').length > 0 ? input.parent('div') : input;
          parentDiv.after(errorElement);
        }

        // Add error styling to input
        input
          .css({
            'border-color': '#d32f2f',
            'box-shadow': '0 0 0 1px rgba(211, 47, 47, 0.2)',
          })
          .addClass('form-field-error');
      }
    } catch (error) {
      console.warn('Error displaying field error for', fieldName, error);
    }
  });
};

// Generic function to clear field errors for any form type
export const clearGenericFieldErrors = (container, fieldName = null) => {
  if (!container) return;

  try {
    if (fieldName) {
      // Clear specific field error
      $(container)
        .find(`.field-error-message[data-field="${fieldName}"]`)
        .remove();
      const input = $(container)
        .find(
          `[name="${fieldName}"], #${fieldName}, [data-field="${fieldName}"]`
        )
        .first();
      if (input.length > 0) {
        input
          .css({
            'border-color': '',
            'box-shadow': '',
          })
          .removeClass('form-field-error');
      }
    } else {
      // Clear all field errors in container
      $(container).find('.field-error-message').remove();
      $(container)
        .find('.form-field-error')
        .each(function () {
          $(this)
            .css({
              'border-color': '',
              'box-shadow': '',
            })
            .removeClass('form-field-error');
        });
    }
  } catch (error) {
    console.warn('Error clearing field errors:', error);
  }
};

// Generic function to attach real-time validation to any form type
export const attachGenericRealTimeValidation = (
  container,
  formType,
  validateFn,
  clearFieldErrorFn,
  addFieldErrorFn
) => {
  if (!container || !formType || typeof validateFn !== 'function') return;

  $(container)
    .find('input, textarea, select')
    .each(function () {
      const input = $(this);
      const fieldName =
        input.attr('name') || input.attr('id') || input.attr('data-field');

      if (fieldName) {
        // Remove existing listeners to avoid duplicates
        input.off(
          'input.genericValidation change.genericValidation blur.genericValidation focus.genericValidation'
        );

        let hasBeenTouched = false;

        input.on('focus.genericValidation', () => {
          hasBeenTouched = true;
        });

        input.on(
          'input.genericValidation change.genericValidation blur.genericValidation',
          (e) => {
            try {
              if (!hasBeenTouched && e.type !== 'blur') {
                return;
              }

              // Get current form data
              const formData = {};
              $(container)
                .find('input, textarea, select')
                .each(function () {
                  const field = $(this);
                  const name =
                    field.attr('name') ||
                    field.attr('id') ||
                    field.attr('data-field');
                  if (name) {
                    const value = field.val();
                    if (field.attr('type') === 'checkbox') {
                      formData[name] = field.is(':checked');
                    } else if (field.attr('type') === 'file') {
                      formData[name] = field[0].files
                        ? Array.from(field[0].files)
                        : [];
                    } else {
                      formData[name] = value;
                    }
                  }
                });

              // Validate current field only
              const validationResult = validateFn(formData);
              const fieldError = validationResult.errors?.fields?.[fieldName];
              const currentlyHasError = input.hasClass('form-field-error');

              if (fieldError && !currentlyHasError) {
                // Show error
                displayGenericFieldErrors(container, formType, {
                  [fieldName]: fieldError,
                });
                if (addFieldErrorFn) {
                  addFieldErrorFn(formType, fieldName, fieldError);
                }
              } else if (!fieldError && currentlyHasError) {
                // Clear error
                clearGenericFieldErrors(container, fieldName);
                if (clearFieldErrorFn) {
                  clearFieldErrorFn(formType, fieldName);
                }
              }
            } catch (error) {
              console.warn(
                'Error in generic field validation for',
                fieldName,
                error
              );
            }
          }
        );
      }
    });
};

// Custom React hook for integrating real-time validation with any form type (DRY principle)
export const useRealTimeValidation = (
  formType,
  validateFn,
  containerRef,
  errors
) => {
  let clearGenericFieldError, addGenericFieldError;

  try {
    const multiFormContext = useMultiForm();
    clearGenericFieldError = multiFormContext?.clearGenericFieldError;
    addGenericFieldError = multiFormContext?.addGenericFieldError;
  } catch (e) {
    // Hook not available - fallback to window methods
    clearGenericFieldError = window.clearGenericFieldError;
    addGenericFieldError = window.addGenericFieldError;
  }

  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !formType || !validateFn) return;

    // Display existing errors
    if (errors?.fields) {
      displayGenericFieldErrors(container, formType, errors.fields);
    }

    // Attach real-time validation
    attachGenericRealTimeValidation(
      container,
      formType,
      validateFn,
      clearGenericFieldError,
      addGenericFieldError
    );

    return () => {
      // Cleanup listeners when component unmounts
      if (container) {
        $(container).find('input, textarea, select').off('.genericValidation');
      }
    };
  }, [
    formType,
    validateFn,
    containerRef,
    errors,
    clearGenericFieldError,
    addGenericFieldError,
  ]);
};

// Export helper function for form value extraction
export { extractFormValues };
